import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './useAuth';
import { getPrimaryProductImageUrl } from '../utils/imageUtils';
import {
  getUserCardGetV2,
  getAddressWithTypeV2,
  addEditAddressV2,
  addressSetDefaultV2,
  // addEditAddressTypeV2,
  deleteUserCardV2,
  addOrderV2,
  deleteAddressV2,
} from './useSupabase';
import type {
  CartItem,
  Address,
  AddressType,
  NewAddressForm,
  OrderFormData,
} from '../components/Cart/types';

export const useCartCheckout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressTypes, setAddressTypes] = useState<AddressType[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [selectedAddressType, setSelectedAddressType] = useState<
    string | number | null
  >(null);
  
  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [showAddAddressTypeModal, setShowAddAddressTypeModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Loading states
  const [hasAddresses, setHasAddresses] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(true);
  const [enhancedLoading, setEnhancedLoading] = useState(false);

  // Order response state
  const [orderResponse, setOrderResponse] = useState<{
    order_id?: number;
    message?: string;
    usd_price?: number;
    items_count?: number;
    total_usd_price?: number;
  } | null>(null);

  // Error response state
  const [errorResponse, setErrorResponse] = useState<{
    message?: string;
  } | null>(null);

  // Form states
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [editingAddressData, setEditingAddressData] = useState<Address | null>(null);
  const [newAddress, setNewAddress] = useState<NewAddressForm>({
    city: "",
    street: "",
    floor: "",
    building: "",
    apartment: "",
    description: "",
    address_type_value: "",
    latitude: 0,
    longitude: 0,
    is_default: false,
  });
  const [newAddressTypeName, setNewAddressTypeName] = useState("");

  const [formData] = useState<OrderFormData>({
    payment_id: 5,
    currency_id: 1,
    currency_rate: 1.0,
    stage_id: 2,
    promo_code: "",
  });

  // Auth check
  useEffect(() => {
    if (!user) {
      navigate("/registration");
    }
  }, [user, navigate]);

  // Fetch cart data
  useEffect(() => {
    const fetchCart = async () => {
      try {
        setCartLoading(true);
        const response = await getUserCardGetV2({});
        const typedResponse = response as any;
        
        if (typedResponse && typedResponse.data) {
          const { items = [] } = typedResponse.data;
          
          if (items.length === 0) {
            setCartItems([]);
            setCartLoading(false);
            return;
          }

          // First set basic cart items
          const basicItems = items.map((item: any) => ({
            id: item.product_id,
            cart_quantity: item.qty,
            name: item.name || "Unknown Product",
            description: item.description || "",
            usd_price: item.usd_price || 0,
            sale_rate: (item.sale_rate || 0) / 100,
            weight: item.weight || 0,
            sub_category_id: item.sub_category_id,
            product_type_id: item.product_type_id,
            product_measurement_id: item.product_measurement_id,
            brand_id: item.brand_id,
            created_at: item.added_at,
            unit_price: item.unit_price || item.usd_price || 0,
            image: "/api/placeholder/300/300",
          }));

          setCartItems(basicItems);
          setCartLoading(false);

          // Enhance with detailed product data
          setEnhancedLoading(true);
          const enhancedPromises = items.map(async (item: any) => {
            try {
              const { data: detailedProduct, error: fetchError } =
                await supabase.rpc("fetch_product_with_images_by_id", {
                  p_product_id: item.product_id,
                  p_locale: "en",
                });

              if (fetchError || !detailedProduct) {
                return basicItems.find((basic: any) => basic.id === item.product_id);
              }

              return {
                id: item.product_id,
                cart_quantity: item.qty,
                name: detailedProduct.name || item.name || "Unknown Product",
                description: detailedProduct.description || item.description || "",
                usd_price: detailedProduct.usd_price || item.usd_price || 0,
                sale_rate: detailedProduct.sale_rate
                  ? detailedProduct.sale_rate / 100
                  : (item.sale_rate || 0) / 100,
                weight: detailedProduct.weight || item.weight || 0,
                sub_category_id: detailedProduct.sub_category_id || item.sub_category_id,
                product_type_id: detailedProduct.product_type_id || item.product_type_id,
                product_measurement_id:
                  detailedProduct.product_measurement_id || item.product_measurement_id,
                brand_id: detailedProduct.brand_id || item.brand_id,
                brand_name: detailedProduct.brand_name || "",
                created_at: detailedProduct.created_at || item.added_at,
                unit_price:
                  detailedProduct.unit_price ||
                  item.unit_price ||
                  detailedProduct.usd_price ||
                  item.usd_price ||
                  0,
                image: getPrimaryProductImageUrl(detailedProduct.image_paths || []),
              };
            } catch (err) {
              return basicItems.find((basic: any) => basic.id === item.product_id);
            }
          });

          const enhancedItems = await Promise.all(enhancedPromises);
          setCartItems(enhancedItems);
          setEnhancedLoading(false);
        } else {
          setCartItems([]);
        }
      } catch (error) {
        setCartItems([]);
      } finally {
        setCartLoading(false);
        setEnhancedLoading(false);
      }
    };

    fetchCart();
    fetchAddressData();
  }, []);

  const fetchAddressData = async () => {
    try {
      setAddressesLoading(true);
      const addressData = await getAddressWithTypeV2({});
      const typedResponse = addressData as any;
      const groups = typedResponse?.data?.groups || [];

      if (groups.length > 0) {
        const allAddresses = groups.reduce((acc: Address[], group: any) => {
          const groupAddresses = group.addresses.map((addr: any) => ({
            ...addr,
            address_type_value: addr.address_type_value || addr.product_type || "",
            address_type_id: group.product_type,
          }));
          return [...acc, ...groupAddresses];
        }, []);

        setAddresses(allAddresses);
        setHasAddresses(true);

        const types = groups.map((group: any, index: number) => ({
          id: group.product_type || `type_${index}`,
          name: group.product_type_label || group.product_type || "",
          value: group.product_type || "",
        }));
        setAddressTypes(types);

        const defaultAddress =
          allAddresses.find((addr: Address) => addr.is_default) || allAddresses[0];
        setSelectedAddress(defaultAddress);
        if (defaultAddress?.address_type_id) {
          setSelectedAddressType(defaultAddress.address_type_id);
        }
      } else {
        setHasAddresses(false);
        setAddresses([]);
        setAddressTypes([
          {
            id: "Home",
            name: "Home",
            value: "Home",
          },
        ]);
        setSelectedAddressType("Home");
      }
    } catch (error) {
      setHasAddresses(false);
      setAddresses([]);
      setAddressTypes([]);
    } finally {
      setAddressesLoading(false);
    }
  };

  // Calculations
  const calculateItemDiscount = (item: CartItem) => {
    const basePrice = item.usd_price || 0;
    const discountAmount = basePrice * (item.sale_rate || 0);
    return discountAmount * item.cart_quantity;
  };

  const calculateItemOriginalPrice = (item: CartItem) => {
    const basePrice = item.usd_price || 0;
    return basePrice * item.cart_quantity;
  };

  const itemsTotal = cartItems.reduce(
    (sum, item) => sum + calculateItemOriginalPrice(item),
    0
  );

  const itemsDiscount = cartItems.reduce(
    (sum, item) => sum + calculateItemDiscount(item),
    0
  );

  const subtotal = itemsTotal - itemsDiscount;
  const shippingCost = 3;
  const estimatedTotal = subtotal + shippingCost;

  // Handlers
  const updateQuantity = (id: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    setCartItems((items) =>
      items.map((item) =>
        item.id === id ? { ...item, cart_quantity: newQuantity } : item
      )
    );
  };

  const removeItem = async (id: number) => {
    try {
      setIsLoading(true);
      await deleteUserCardV2({ product_id: id });
      setCartItems((items) => items.filter((item) => item.id !== id));
    } catch (error) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddressChange = (
    field: keyof NewAddressForm,
    value: string | number | boolean
  ) => {
    setNewAddress((prev) => ({ ...prev, [field]: value }));
  };



  const handleAddAddress = async () => {
    if (!newAddress.street.trim() || !newAddress.city.trim()) {
      return;
    }

    setShowAddAddressModal(false);
    try {
      setIsLoading(true);
      const payload: any = {
        city: newAddress.city.trim(),
        street: newAddress.street.trim(),
        floor: newAddress.floor.trim() || null,
        building: newAddress.building.trim() || null,
        apartment: newAddress.apartment.trim() || null,
        description: newAddress.description.trim() || null,
        latitude: newAddress.latitude || 0,
        longitude: newAddress.longitude || 0,
        is_default: newAddress.is_default,
        address_type_value: newAddress.address_type_value, // Always include address_type_value
      };
      
      if (editingAddressId) {
        payload.id = editingAddressId;
      }
      
      const response = await addEditAddressV2(payload);

      const typedResponse = response as any;
      
      // Only proceed if the response is successful
      if (typedResponse && typedResponse.status === "success" && !typedResponse.error) {
        // Reset the form
        setNewAddress({
          city: "",
          street: "",
          floor: "",
          building: "",
          apartment: "",
          description: "",
          latitude: 0,
          longitude: 0,
          address_type_value: "",
          is_default: false,
        });

        // Reset editing state
        setEditingAddressId(null);
        setEditingAddressData(null);

        setShowAddAddressModal(false);
        
        // Fetch updated address data from the database
        await fetchAddressData();
        
        // The selectedAddress will be set in fetchAddressData() based on default or first address
        setHasAddresses(true);
      } else {
        // Handle error - show error message to user
        setErrorResponse({
          message: typedResponse?.message || "Failed to add/edit address. Please try again.",
        });
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error adding/editing address:", error);
      setErrorResponse({
        message: "Error adding/editing address. Please try again.",
      });
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // const handleAddAddressType = async () => {
  //   if (!newAddressTypeName.trim()) {
  //     return;
  //   }

  //   setIsLoading(true);
  //   try {
  //     await addEditAddressTypeV2({ name: newAddressTypeName.trim() });

  //     const newType = {
  //       id: newAddressTypeName.trim(),
  //       name: newAddressTypeName.trim(),
  //       value: newAddressTypeName.trim(),
  //     };

  //     setAddressTypes((prev) => [...prev, newType]);
  //     setSelectedAddressType(newType.id);
  //     setNewAddress((prev) => ({
  //       ...prev,
  //       address_type_id: newType.id,
  //       address_type_value: newType.value,
  //     }));

  //     setNewAddressTypeName("");
  //     setShowAddAddressTypeModal(false);
  //     setShowAddAddressModal(true);
  //   } catch (error) {
  //     // Handle error
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleDeleteAddress = async (addressId: number) => {
    setIsLoading(true);
    try {
      await deleteAddressV2({ id: addressId });
      
      // Check if the deleted address was the selected one
      if (selectedAddress?.id === addressId) {
        setSelectedAddress(null);
      }
      
      await fetchAddressData();
    } catch (error) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefaultAddress = async (addressId: number) => {
    setIsLoading(true);
    try {
      await addressSetDefaultV2({ id: addressId });
      await fetchAddressData();
    } catch (error) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      alert("Your cart is empty.");
      return;
    }
    setShowCheckoutModal(true);
  };

  const handleConfirmOrder = async () => {
    if (!selectedAddress) {
      alert("Please select a shipping address.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        address_value: {
          street: selectedAddress.street || "",
          city: selectedAddress.city || "",
          building: selectedAddress.building || "",
          floor: selectedAddress.floor || "",
          apartment: selectedAddress.apartment || "",
          description: selectedAddress.description || "",
        },
        payment_id: formData.payment_id,
        currency_id: formData.currency_id,
        currency_rate: formData.currency_rate,
        items: cartItems.map((item) => ({
          product_id: item.id,
          quantity: item.cart_quantity,
          price: item.usd_price || 0,
          discount_rate: item.sale_rate || 0,
          product_measurement_id: item.product_measurement_id || 1,
        })),
        stage_id: formData.stage_id,
        promo_code_value: formData.promo_code
          ? {
              code: formData.promo_code,
              discount: 0.1,
            }
          : null,
      };

      const response = await addOrderV2(payload);
      const typedResponse = response as any;
      
      // Check if response has the expected success format
      if (typedResponse && typedResponse.status === "success" && typedResponse.data) {
        // Store the order response data
        setOrderResponse({
          order_id: typedResponse.data.order_id,
          message: typedResponse.message,
          usd_price: typedResponse.data.usd_price,
          items_count: typedResponse.data.items_count,
          total_usd_price: typedResponse.data.total_usd_price,
        });
        
        setCartItems([]);
        setShowCheckoutModal(false);
        setShowConfirmationModal(true);
      } else if (typedResponse && typedResponse.status === "error") {
        // Handle error response
        setErrorResponse({
          message: typedResponse.message || "An error occurred while processing your order.",
        });
        setShowErrorModal(true);
      } else {
        // Fallback for unexpected response format
        setErrorResponse({
          message: "Error creating order. Please try again.",
        });
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      setErrorResponse({
        message: "Error submitting order. Please try again.",
      });
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return {
    // State
    cartItems,
    addresses,
    addressTypes,
    selectedAddress,
    selectedAddressType,
    hasAddresses,
    addressesLoading,
    editingAddressId,
    editingAddressData,
    newAddress,
    newAddressTypeName,
    
    // Modal states
    showAddressModal,
    showAddAddressModal,
    showAddAddressTypeModal,
    showCheckoutModal,
    showConfirmationModal,
    showErrorModal,
    
    // Loading states
    isLoading,
    cartLoading,
    enhancedLoading,

    // Order response
    orderResponse,
    
    // Error response
    errorResponse,    // Calculations
    itemsTotal,
    itemsDiscount,
    subtotal,
    shippingCost,
    estimatedTotal,
    
    // Handlers
    updateQuantity,
    removeItem,
    handleAddressChange,
    handleAddAddress,
    // handleAddAddressType,
    handleDeleteAddress,
    handleSetDefaultAddress,
    handleCheckout,
    handleConfirmOrder,
    formatCurrency,
    
    // Setters
    setSelectedAddress,
    setSelectedAddressType,
    setShowAddressModal,
    setShowAddAddressModal,
    setShowAddAddressTypeModal,
    setShowCheckoutModal,
    setShowConfirmationModal,
    setShowErrorModal,
    setNewAddressTypeName,
    setEditingAddressId,
    setEditingAddressData,
    setNewAddress
  };
};
